const _ = require("lodash");

/**
 * Wraps setTimeout and clearTimeout, allowing them to be paused or even mocked
 * with sync calls advancing an internal timer. Used by the socket classes.
 *
 * ---
 *
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const ActiveTimeouts = function(props = {}) {
	if (!(this instanceof ActiveTimeouts)) {
		return new ActiveTimeouts(props);
	}
	this.minActiveLength = props.minActiveLength || 1000;
	this.mockingTime = !!props.mockingTime;

	this.mockNow = 0;
	this.nextID = 1;
	this.timeoutIDTable = {};
	this.active = true;
};

const fireID = function(aTO, id) {
	if (!aTO.timeoutIDTable[id]) {
		return;
	}
	const {cb} = aTO.timeoutIDTable[id];
	delete aTO.timeoutIDTable[id];
	cb();
};

// Advance the mock clock. Throws if mockingTime is false.
ActiveTimeouts.prototype.advanceMockTime = function(ms) {
	if (!this.mockingTime) {
		throw new Error("Can only use advanceMockTime if mockingTime is true.");
	}
	const newNow = this.mockNow + ms;
	const isActive = data => this.active || data.alwaysActive;
	while (true) {
		const activePairs = _.toPairs(this.timeoutIDTable).filter(([id, data]) =>
			isActive(data)
		);
		const first = _.minBy(
			activePairs,
			([id, data]) => data.startedAt + data.timeLeft
		);
		if (!first) {
			break;
		}
		const finishTime = first[1].startedAt + first[1].timeLeft;
		if (finishTime > newNow) {
			break;
		}
		this.mockNow = finishTime;
		// Note that timeoutIDTable may mutate as a result of this call.
		fireID(this, first[0]);
	}

	_.each(this.timeoutIDTable, (data, id) => {
		if (!isActive(data)) {
			return;
		}
		const diff = newNow - data.startedAt;
		data.timeLeft -= diff;
		data.startedAt += diff;
	});
	this.mockNow = newNow;
};

ActiveTimeouts.prototype.setActive = function(newVal) {
	const now = this.mockingTime ? this.mockNow : Date.now();
	newVal = !!newVal;
	if (this.active === newVal) {
		return; // Do nothing
	}
	_.each(this.timeoutIDTable, (data, id) => {
		if (data.alwaysActive) {
			// No change needed
		} else if (newVal) {
			if (!this.mockingTime) {
				data.setTimeoutID = setTimeout(() => fireID(this, id), data.timeLeft);
			}
			data.startedAt = now;
		} else if (!newVal) {
			if (!this.mockingTime) {
				clearTimeout(data.setTimeoutID);
			}
			data.timeLeft = Math.max(
				this.minActiveLength,
				data.timeLeft - (now - data.startedAt)
			);
		}
	});
	this.active = newVal;
};

ActiveTimeouts.prototype.setTimeout = function(cb, duration, options = {}) {
	const {alwaysActive = false} = options;
	const id = this.nextID;
	this.nextID += 1;

	const now = this.mockingTime ? this.mockNow : Date.now();
	const timeoutIsActive = alwaysActive || this.active;
	let setTimeoutID = null;
	if (!this.mockingTime && timeoutIsActive) {
		setTimeoutID = setTimeout(() => fireID(this, id), duration);
	}

	this.timeoutIDTable[id] = {
		cb,
		alwaysActive,
		startedAt: now,
		timeLeft: duration,
		setTimeoutID,
	};

	return id;
};

ActiveTimeouts.prototype.clearTimeout = function(id) {
	const data = this.timeoutIDTable[id];
	if (data) {
		clearTimeout(data.setTimeoutID);
		delete this.timeoutIDTable[id];
	}
};

module.exports = ActiveTimeouts;
